import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { formatMediaBytes, isOssStorageMode, mediaUsagePercent } from '@/lib/mediaStorage';
import type { MediaStorageStatus } from '@/lib/mediaStorage';

type Props = {
  status: MediaStorageStatus;
  /** 顶栏浅色背景 */
  variant?: 'sidebar' | 'header';
};

export function StorageUsageIndicator({ status, variant = 'sidebar' }: Props) {
  const isHeader = variant === 'header';

  if (isOssStorageMode(status.storage_mode)) {
    return (
      <div
        className={cn(
          'rounded-lg border px-2.5 py-1.5',
          isHeader ? 'border-teal-200/80 bg-teal-50/90' : 'border-teal-500/30 bg-teal-950/40',
        )}
      >
        <p className={cn('text-[10px] font-semibold uppercase tracking-wide', isHeader ? 'text-teal-700' : 'text-teal-400/90')}>
          多媒体
        </p>
        <p className={cn('text-[11px] font-medium', isHeader ? 'text-teal-900' : 'text-teal-100')}>OSS 已启用</p>
      </div>
    );
  }

  if (status.quota_bytes == null) {
    return null;
  }

  const pct = mediaUsagePercent(status.usage_bytes, status.quota_bytes);
  const barColor = status.at_quota ? 'bg-rose-500' : status.near_quota ? 'bg-amber-400' : 'bg-teal-500';

  return (
    <div
      className={cn(
        'min-w-[8.5rem] rounded-lg border px-2.5 py-1.5',
        isHeader
          ? status.at_quota
            ? 'border-rose-200 bg-rose-50/90'
            : status.near_quota
              ? 'border-amber-200 bg-amber-50/90'
              : 'border-slate-200 bg-slate-50/95'
          : status.at_quota
            ? 'border-rose-500/40 bg-rose-950/50'
            : status.near_quota
              ? 'border-amber-500/35 bg-amber-950/40'
              : 'border-slate-600/80 bg-slate-900/50',
      )}
    >
      <p className={cn('text-[10px] font-semibold uppercase tracking-wide', isHeader ? 'text-slate-500' : 'text-slate-400')}>
        本地多媒体
      </p>
      <p className={cn('text-[11px] font-semibold', isHeader ? 'text-slate-800' : 'text-slate-100')}>
        {formatMediaBytes(status.usage_bytes)}
        <span className={cn('font-normal', isHeader ? 'text-slate-500' : 'text-slate-400')}>
          {' '}
          / {formatMediaBytes(status.quota_bytes)}
        </span>
      </p>
      <div className={cn('mt-1 h-1 overflow-hidden rounded-full', isHeader ? 'bg-slate-200' : 'bg-slate-800')}>
        <div className={cn('h-full rounded-full transition-all', barColor)} style={{ width: `${pct}%` }} />
      </div>
      {status.near_quota || status.at_quota ? (
        <Link
          to="/oss"
          className={cn(
            'mt-0.5 block text-[10px] font-medium underline underline-offset-2',
            isHeader ? 'text-teal-700 decoration-teal-700/30 hover:text-teal-800' : 'text-teal-300 decoration-teal-400/40 hover:text-teal-200',
          )}
        >
          {status.at_quota ? '空间已满 · 配置 OSS' : '建议切换 OSS →'}
        </Link>
      ) : isHeader ? (
        <p className="mt-0.5 text-[10px] text-slate-400">可迁移至 OSS</p>
      ) : (
        <p className="mt-1 text-[10px] text-slate-500">推荐迁移至 OSS</p>
      )}
    </div>
  );
}
